import { FunctionComponent, useEffect, useMemo } from 'react'
import Router from '@/router'
import '@/styles/reset.css'
import '@/styles/global.css'
import {
  ThemeProvider,
  ToastContainer as OldToastContainer,
} from '@conexasaude/hero'
import {
  EnvironmentIdentifier,
  ThemeProvider as ThemeProviderConexaHero,
  ToastContainer as ToastContainerConexaHero,
} from '@conexasaude/conexa-hero'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { ToastContainer } from '@conexasaude/hero-web'
import { Global } from '@emotion/react'
import { useGlobalStyles } from '@/styles/global'
import '@/styles/bootstrap-grid-no-compress.css'
import '@/styles/bootstrap-utilities-compressed.css'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { MedicalSpecialitiesProvider } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { ModalProvider } from '@/components/Modal/contexts/ModalContext'
import { ModalProvider as ModalProviderConexaHero } from '@/components/RebrandedModal/contexts/ModalContext'
import { AttendanceProvider } from '@/modules/Attendance/contexts/AttendanceContex'
import { CheckoutProvider } from '@/modules/Checkout/contexts/CheckoutContext'
import ScreeningProvider from '@/modules/Screening/contexts/screeningContext'
import { RealtimeAttendanceProvider } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { FirebaseProvider } from '@/modules/Firebase/contexts/FirebaseContext'
import RealtimeAttendance from '@/modules/RealtimeAttendance/pages'
// import { Zendesk } from '@/components/Zendesk'
import { PatientRegistrationProvider } from './modules/PatientRegistration/contexts/PatientRegistrationContext'
import { BenefitsProvider } from './modules/Benefits/contexts/BenefitsContext'
import { ConciergeAps } from '@/modules/HealthTeams/components/ConciergeAps'
import { RenderIf } from './components/RenderIf'
import { useAuthContext } from './modules/Auth/contexts/AuthContext'
import { GroupConsultationProvider } from './modules/MyCalls/contexts/consultationGroupContext'
import { startBrazeSdk } from './services/braze'
// import { ChatLayer } from './components/ChatLayer'
import { GlobalActions } from './components/GlobalActions'
import useHeight from './utils/useHeight'
import browserUpdate from 'browser-update'
import { useLocation } from 'react-router-dom'
import { AnalyticsProvider, AppDataSync } from '@conexasaude/analytics'
import { ReservationConsultationProvider } from './modules/MyCalls/contexts/consultationReservationContext'
import { useRebrandingThemeGeneratorContext } from './rebrandedTheme/contexts/ThemeGeneratorContext'
import { IS_PRODUCTION } from './constants'

// Needed for hero-web toast
import 'animate.css'

const queryClient = new QueryClient()

const App: FunctionComponent = () => {
  const { theme } = useThemeGeneratorContext()
  const { theme: rebrandedTeme } = useRebrandingThemeGeneratorContext()
  const { user } = useAuthContext()
  const { pathname } = useLocation()

  const height = useHeight()
  const styles = useGlobalStyles(theme, { height })
  const hasHealthTeamEnabled = useMemo(() => user?.isHealthTeam, [user])

  const analyticsDataSync: AppDataSync = useMemo(
    () => ({
      user,
      pathname,
      devEnvironment: process.env.REACT_APP_ENV_NAME !== 'PROD',
      gtag: {
        default: true,
      },
    }),
    [user, pathname],
  )

  useEffect(() => {
    if (!user) return

    startBrazeSdk(user)
  }, [user])

  useEffect(() => {
    browserUpdate({
      required: {
        c: -1,
        f: -1,
        s: -1,
        e: -1,
        e_a: -1,
        i: -1,
        ios: -1,
        samsung: -1,
        o: -1,
        o_a: -1,
        y: -1,
        v: -1,
        uc: -1,
        a: -1,
        silk: -1,
        waterfox: -1,
        palemoon: -1,
      },
      // insecure: true,
      // unsupported: true,
      reminder: 24,
      reminderClosed: 48,
      // no_permanent_hide: true,

      text: {
        msg: 'Seu navegador da web ({brow_name}) está desatualizado.',
        msgmore:
          'Atualize seu navegador para mais segurança, velocidade e melhor experiência deste site',
        bupdate: 'Atualizar navegador',
        bignore: 'Agora não',
        remind: 'Você será lembrado novamente em {days} dias.',
        bnever: 'Nunca mostrar novamente',
      },
    })
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProviderConexaHero theme={rebrandedTeme}>
        <ThemeProvider theme={theme}>
          <MedicalSpecialitiesProvider>
            <AttendanceProvider>
              <PatientRegistrationProvider>
                <CheckoutProvider>
                  <ModalProviderConexaHero>
                    <ModalProvider>
                      <RealtimeAttendanceProvider>
                        <ScreeningProvider>
                          <AnalyticsProvider appDataSync={analyticsDataSync}>
                            <FirebaseProvider>
                              <BenefitsProvider>
                                <GroupConsultationProvider>
                                  <ReservationConsultationProvider>
                                    <div className="environmentIdentifier">
                                      <EnvironmentIdentifier
                                        disabled={IS_PRODUCTION}
                                      />
                                    </div>
                                    <div
                                      css={[
                                        styles.mainContainer,
                                        styles.typography,
                                      ]}
                                    >
                                      <Router />
                                      <RealtimeAttendance />
                                      <div id="modal-root" />
                                      <div id="hero-modal-root" />
                                      <RenderIf
                                        condition={hasHealthTeamEnabled}
                                      >
                                        <ConciergeAps />
                                      </RenderIf>
                                      {/* <Zendesk />
                                <ChatLayer /> */}
                                      <GlobalActions />
                                    </div>
                                  </ReservationConsultationProvider>
                                </GroupConsultationProvider>
                              </BenefitsProvider>
                            </FirebaseProvider>
                          </AnalyticsProvider>
                        </ScreeningProvider>
                      </RealtimeAttendanceProvider>
                    </ModalProvider>
                  </ModalProviderConexaHero>
                </CheckoutProvider>
                <OldToastContainer
                  theme="dark"
                  autoClose={5000}
                  position="top-right"
                />
                <ToastContainer position="top-right" />
                <ToastContainerConexaHero position="top-right" />
                <Global styles={styles.global} />
              </PatientRegistrationProvider>
            </AttendanceProvider>
          </MedicalSpecialitiesProvider>
        </ThemeProvider>
      </ThemeProviderConexaHero>
    </QueryClientProvider>
  )
}

export default App
